import { LuFile, LuImageOff } from 'react-icons/lu';

interface Props {
  url: string | null;
  mimeType: string | null;
  /** The exact WhatsApp message text (the server returns the raw source object
   *  verbatim as `sourceText`). Preferred over iframing the S3 file for text/*
   *  sources — rendering the string ourselves keeps it under our charset control
   *  rather than depending on how the browser sniffs the object. */
  text?: string | null;
}

// Friendly label for document types that have no inline viewer.
function docLabel(mime: string): string {
  if (mime.includes('word') || mime === 'application/msword') return 'Word document';
  if (mime.includes('sheet') || mime.includes('excel')) return 'Spreadsheet';
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return 'Presentation';
  if (mime === 'application/zip') return 'Archive';
  return mime || 'Document';
}

/**
 * Renders the source of a staging row based on its mime type — the WhatsApp
 * pipeline stores photos, PDFs, text messages, or other documents. Images render
 * as a picture; PDFs inline via an iframe; text messages render as the exact
 * message string (we control its encoding); anything else gets a download link.
 */
export function SourcePreview({ url, mimeType, text }: Props) {
  const mime = mimeType ?? '';
  const isText = mime.startsWith('text/');

  // Text message: render the exact message string (verbatim from the server),
  // not the S3 object directly. Falls back to the iframe only if we have no text.
  if (isText && text) {
    return (
      <pre className="h-full max-h-[440px] w-full overflow-auto whitespace-pre-wrap px-4 py-3 text-left font-sans text-[13px] leading-relaxed text-foreground">
        {text}
      </pre>
    );
  }

  if (!url) {
    if (isText) {
      return (
        <div className="flex flex-col items-center gap-2 text-foreground-muted">
          <LuImageOff size={28} />
          <span className="text-[12px]">No message text</span>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-2 text-foreground-muted">
        <LuImageOff size={28} />
        <span className="text-[12px]">No source</span>
      </div>
    );
  }

  if (mime.startsWith('image/')) {
    return (
      <img
        src={url}
        alt="Source price board"
        className="max-h-[440px] w-full object-contain"
      />
    );
  }

  if (mime === 'application/pdf') {
    return (
      <iframe
        title="Source PDF"
        src={url}
        className="h-[440px] w-full border-0 bg-white"
      />
    );
  }

  if (isText) {
    return (
      <iframe
        title="Source message"
        src={url}
        className="h-[440px] w-full border-0 bg-white"
      />
    );
  }

  // Word / Excel / other — no browser-native inline viewer.
  return (
    <div className="flex flex-col items-center gap-2 px-4 text-center text-foreground-muted">
      <LuFile size={28} />
      <span className="text-[12px]">{docLabel(mime)} — no inline preview</span>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-[12px] font-medium text-primary underline"
      >
        Open / download
      </a>
    </div>
  );
}
